import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

/** Clears any stale Supabase auth tokens from localStorage */
export function clearSupabaseAuthTokens() {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('sb-') || key.includes('supabase'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
    console.log('[Supabase] cleared stale auth tokens from localStorage');
  } catch {
    // localStorage may not be available in some environments
  }
}

/** Returns true if the error message indicates an invalid/expired refresh token */
function isRefreshTokenError(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes('refresh token not found') ||
    m.includes('invalid refresh token') ||
    m.includes('refresh_token_not_found') ||
    m.includes('token has expired') ||
    m.includes('token is expired')
  );
}

/** Returns true if the error is an empty object (network/CORS error) */
function isEmptyError(err: unknown): boolean {
  if (!err) return true;
  if (typeof err === 'object') {
    try {
      return JSON.stringify(err) === '{}';
    } catch {
      return false;
    }
  }
  return false;
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  global: {
    fetch: async (url, options) => {
      const urlStr = typeof url === 'string' ? url : url instanceof URL ? url.toString() : (url as Request).url;
      const isRefreshRequest = urlStr.includes('grant_type=refresh_token');
      try {
        const res = await fetch(url, options);
        return res;
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        const empty = isEmptyError(err);

        if (isRefreshRequest || empty || isRefreshTokenError(msg)) {
          // Silently clear stale tokens — TOKEN_REFRESH_FAILED event will fire
          // and AuthContext will show the SessionExpiredBanner to the user.
          clearSupabaseAuthTokens();
          console.warn('[Supabase] refresh token fetch failed — tokens cleared, session will expire gracefully');
        } else {
          console.warn('[Supabase] fetch failed (network):', msg);
        }
        return Promise.reject(err);
      }
    },
  },
});

// ── Listen for TOKEN_REFRESH_FAILED at the client level ──────────────────────
// This fires when Supabase's background auto-refresh fails (e.g. revoked token).
supabase.auth.onAuthStateChange((event) => {
  if ((event as string) === 'TOKEN_REFRESH_FAILED') {
    console.warn('[Supabase] TOKEN_REFRESH_FAILED — clearing stale tokens');
    clearSupabaseAuthTokens();
    // The AuthContext listener will also catch this and set sessionExpired=true,
    // so we don't need to reload here — the UI will handle it.
  }
});

export type Database = {
  profiles: {
    id: string;
    name: string;
    full_name: string;
    phone: string;
    currency: string;
    timezone: string;
    goal: string;
    plan: 'free' | 'paid';
    has_bank_account: boolean;
    has_credit_card: boolean;
    created_at: string;
    updated_at: string;
  };
  accounts: {
    id: string;
    user_id: string;
    name: string;
    type: 'corrente' | 'digital' | 'poupanca' | 'carteira' | 'investimento';
    bank: string;
    initial_balance: number;
    current_balance: number;
    color: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  };
  transactions: {
    id: string;
    user_id: string;
    account_id: string | null;
    description: string;
    category: string;
    type: 'receita' | 'despesa';
    amount: number;
    date: string;
    status: 'pago' | 'pendente' | 'vencido';
    notes: string;
    created_at: string;
    updated_at: string;
  };
  cards: {
    id: string;
    user_id: string;
    name: string;
    bank: string;
    limit_amount: number;
    closing_day: number;
    due_day: number;
    color: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  };
  goals: {
    id: string;
    user_id: string;
    name: string;
    target_amount: number;
    current_amount: number;
    target_date: string | null;
    color: string;
    icon: string;
    is_completed: boolean;
    created_at: string;
    updated_at: string;
  };
};
